/** Razorpay checkout helper. Loads Razorpay JS dynamically and opens checkout.
 *  Falls back to silent mock activation when keys are not configured. */
import api from "@/lib/api";

let scriptLoaded = false;

function loadScript() {
  if (scriptLoaded) return Promise.resolve(true);
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => { scriptLoaded = true; resolve(true); };
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/** Open Razorpay checkout for a package activation.
 * Returns { ok, order_no, mock? } on success. */
export async function payForPackage({ packageCode, user, onSuccess, onError }) {
  try {
    const { data: cfg } = await api.get("/payments/config");
    const { data: order } = await api.post("/payments/order", { kind: "package", package_code: packageCode });

    if (!cfg.live || order.mock) {
      // Mock path
      const { data } = await api.post("/payments/verify", {
        kind: "package",
        package_code: packageCode,
        razorpay_order_id: order.id,
        razorpay_payment_id: `mock_${Date.now()}`,
        razorpay_signature: "mock",
      });
      onSuccess?.({ ...data, mock: true });
      return;
    }

    const ok = await loadScript();
    if (!ok) throw new Error("Failed to load Razorpay");

    const rzp = new window.Razorpay({
      key: cfg.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.id,
      name: "Samara",
      description: `Package ${packageCode}`,
      prefill: { name: user?.name, email: user?.email, contact: user?.phone || "" },
      theme: { color: "#1B2A4E" },
      handler: async (response) => {
        try {
          const { data } = await api.post("/payments/verify", {
            kind: "package",
            package_code: packageCode,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          onSuccess?.(data);
        } catch (e) { onError?.(e); }
      },
      modal: { ondismiss: () => onError?.(new Error("Cancelled")) },
    });
    rzp.open();
  } catch (e) { onError?.(e); }
}

/** Pay for a cart checkout via Razorpay (with mock fallback). */
export async function payForCart({ items, shipping_address, amount, user, onSuccess, onError }) {
  try {
    const { data: cfg } = await api.get("/payments/config");
    const { data: order } = await api.post("/payments/order", { kind: "cart", amount });

    const payloadForServer = { items, shipping_address };

    if (!cfg.live || order.mock) {
      const { data } = await api.post("/payments/verify", {
        kind: "cart",
        order_payload: payloadForServer,
        razorpay_order_id: order.id,
        razorpay_payment_id: `mock_${Date.now()}`,
        razorpay_signature: "mock",
      });
      onSuccess?.({ ...data, mock: true });
      return;
    }

    const ok = await loadScript();
    if (!ok) throw new Error("Failed to load Razorpay");
    const rzp = new window.Razorpay({
      key: cfg.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.id,
      name: "Samara",
      description: "Order checkout",
      prefill: { name: user?.name, email: user?.email, contact: user?.phone || "" },
      theme: { color: "#1B2A4E" },
      handler: async (response) => {
        try {
          const { data } = await api.post("/payments/verify", {
            kind: "cart",
            order_payload: payloadForServer,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          onSuccess?.(data);
        } catch (e) { onError?.(e); }
      },
      modal: { ondismiss: () => onError?.(new Error("Cancelled")) },
    });
    rzp.open();
  } catch (e) { onError?.(e); }
}
